import type { ClientSettings, ClientSettingsGoal } from '@/api/smartMarketplace';

export const ACOS_PERCENTS = [10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 75, 100] as const;
export const ROAS_VALUES = [1.5, 2, 2.5, 3, 4, 5, 6, 8, 10] as const;
export const CHANGE_PCTS = [5, 10, 15, 20, 25, 30, 40, 50] as const;
export const PLACEMENT_PCTS = [0, 25, 50, 75, 100, 150, 200, 300, 400, 500, 900] as const;

const GOALS: readonly ClientSettingsGoal[] = ['target_acos', 'target_roas', 'efficiency'];

export type ClientSettingsFormState = {
  goal: ClientSettingsGoal | '';
  targetAcosPercent: string;
  targetRoas: string;
  maxBid: string;
  maxBudget: string;
  minPlacement: string;
  maxPlacement: string;
};

export function emptyClientSettingsForm(): ClientSettingsFormState {
  return {
    goal: '',
    targetAcosPercent: '',
    targetRoas: '',
    maxBid: '',
    maxBudget: '',
    minPlacement: '',
    maxPlacement: '',
  };
}

function isGoal(value: string | null | undefined): value is ClientSettingsGoal {
  return GOALS.some((goal) => goal === value);
}

function matchNumber(value: number | null | undefined, options: readonly number[]): string {
  if (value == null || !Number.isFinite(value)) return '';
  const found = options.find((option) => Math.abs(option - value) < 1e-6);
  return String(found ?? value);
}

export function selectValues(presets: readonly number[], current: string): number[] {
  if (current === '') return [...presets];
  const value = Number(current);
  if (!Number.isFinite(value)) return [...presets];
  if (presets.some((preset) => Math.abs(preset - value) < 1e-6)) return [...presets];
  return [...presets, value].sort((left, right) => left - right);
}

export function formFromSettings(settings: ClientSettings | null | undefined): ClientSettingsFormState {
  const objective = settings?.objective;
  const constraints = settings?.constraints;
  const goal = isGoal(objective?.goal) ? objective.goal : '';
  const acosPercent = objective?.target_acos == null ? null : Math.round(objective.target_acos * 100);
  return {
    goal,
    targetAcosPercent: goal === 'target_acos' ? matchNumber(acosPercent, ACOS_PERCENTS) : '',
    targetRoas: goal === 'target_roas' ? matchNumber(objective?.target_roas, ROAS_VALUES) : '',
    maxBid: matchNumber(constraints?.max_bid_change_pct, CHANGE_PCTS),
    maxBudget: matchNumber(constraints?.max_budget_change_pct, CHANGE_PCTS),
    minPlacement: matchNumber(constraints?.min_placement_multiplier, PLACEMENT_PCTS),
    maxPlacement: matchNumber(constraints?.max_placement_multiplier, PLACEMENT_PCTS),
  };
}

export function placementRangeInvalid(form: ClientSettingsFormState): boolean {
  return (
    form.minPlacement !== '' &&
    form.maxPlacement !== '' &&
    Number(form.minPlacement) > Number(form.maxPlacement)
  );
}

export function clientSettingsCanSave(form: ClientSettingsFormState): boolean {
  if (!form.goal) return false;
  if (form.goal === 'target_acos' && form.targetAcosPercent === '') return false;
  if (form.goal === 'target_roas' && form.targetRoas === '') return false;
  if (placementRangeInvalid(form)) return false;
  return true;
}

function optionalNumber(value: string): number | null {
  if (value === '') return null;
  return Number(value);
}

export function toClientSettings(form: ClientSettingsFormState): ClientSettings {
  if (!form.goal) {
    throw new Error('An optimization objective is required.');
  }
  return {
    objective: {
      goal: form.goal,
      target_acos: form.goal === 'target_acos' ? Number(form.targetAcosPercent) / 100 : null,
      target_roas: form.goal === 'target_roas' ? Number(form.targetRoas) : null,
    },
    constraints: {
      max_bid_change_pct: optionalNumber(form.maxBid),
      max_budget_change_pct: optionalNumber(form.maxBudget),
      min_placement_multiplier: optionalNumber(form.minPlacement),
      max_placement_multiplier: optionalNumber(form.maxPlacement),
    },
  };
}
